import React from 'react';
import { MapPin, Phone, Mail, Clock, Globe, Facebook, Instagram, Star } from 'lucide-react';

const VetrinaCard = ({ vetrina }) => {
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path?.startsWith('http')) return path;
    return `${import.meta.env?.VITE_SUPABASE_URL}/storage/v1/object/public/vetrine-images/${path}`;
  };

  const logoUrl = getImageUrl(vetrina?.logo_negozio);
  const mainImageUrl = getImageUrl(vetrina?.immagine_principale);
  const galleryImages = vetrina?.gallery_prodotti?.map(img => getImageUrl(img))?.filter(Boolean) || [];

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
      {/* Demo Badge */}
      {vetrina?.tipo_vetrina === 'demo' && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 px-3 py-2">
          <p className="text-xs font-semibold text-yellow-800 flex items-center gap-1">
            <span>⚠️</span>
            <span>VETRINA DEMO - Esempio per riunioni</span>
          </p>
        </div>
      )}

      {/* Image Section */}
      <div className="relative h-64 bg-gradient-to-br from-blue-500 to-purple-600">
        {mainImageUrl ? (
          <img
            src={mainImageUrl}
            alt={`Vetrina principale di ${vetrina?.nome_negozio}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-white text-xl font-semibold">Nessuna immagine</span>
          </div>
        )}
        
        {logoUrl && (
          <div className="absolute bottom-4 left-4 w-20 h-20 bg-white rounded-full shadow-lg overflow-hidden border-4 border-white">
            <img
              src={logoUrl}
              alt={`Logo di ${vetrina?.nome_negozio}`}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="absolute top-4 right-4">
          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-gray-800">
            {vetrina?.categoria}
          </span>
        </div>
      </div>

      <div className="p-6">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {vetrina?.nome_negozio}
          </h2>
          
          {vetrina?.rating && (
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="ml-1 font-semibold text-gray-900">{vetrina?.rating}</span>
              </div>
              {vetrina?.numero_recensioni > 0 && (
                <span className="text-sm text-gray-500">({vetrina?.numero_recensioni} recensioni)</span>
              )}
            </div>
          )}
        </div>

        {vetrina?.descrizione_breve && (
          <p className="text-gray-600 mb-4 leading-relaxed">
            {vetrina?.descrizione_breve}
          </p>
        )}

        <div className="space-y-3 mb-4">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-900 font-medium">{vetrina?.indirizzo}</p>
              <p className="text-sm text-gray-500">{vetrina?.zona}</p>
            </div>
          </div>

          {vetrina?.telefono && (
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-green-600 flex-shrink-0" />
              <a href={`tel:${vetrina?.telefono}`} className="text-gray-900 hover:text-blue-600 transition-colors">
                {vetrina?.telefono}
              </a>
            </div>
          )}

          {vetrina?.email && (
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-red-600 flex-shrink-0" />
              <a href={`mailto:${vetrina?.email}`} className="text-gray-900 hover:text-blue-600 transition-colors">
                {vetrina?.email}
              </a>
            </div>
          )}

          {vetrina?.orari_apertura && (
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Orari di apertura</p>
                <p className="text-gray-900 whitespace-pre-line">{vetrina?.orari_apertura}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          {vetrina?.sito_web && (
            <a
              href={vetrina?.sito_web}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">Sito Web</span>
            </a>
          )}

          {vetrina?.facebook && (
            <a
              href={vetrina?.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Facebook className="w-4 h-4" />
              <span className="text-sm font-medium">Facebook</span>
            </a>
          )}

          {vetrina?.instagram && (
            <a
              href={vetrina?.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors"
            >
              <Instagram className="w-4 h-4" />
              <span className="text-sm font-medium">Instagram</span>
            </a>
          )}
        </div>

        {galleryImages?.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Galleria Prodotti</h3>
            <div className="grid grid-cols-3 gap-2">
              {galleryImages?.slice(0, 6)?.map((imageUrl, index) => (
                <div key={index} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={imageUrl}
                    alt={`Prodotto ${index + 1} di ${vetrina?.nome_negozio}`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
            {galleryImages?.length > 6 && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                +{galleryImages?.length - 6} altre immagini
              </p>
            )}
          </div>
        )}

        {(vetrina?.servizi_consegna || vetrina?.servizi_prenotazione || vetrina?.servizi_appuntamento || vetrina?.servizi_pagamenti_digitali) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Servizi disponibili</h3>
            <div className="flex flex-wrap gap-2">
              {vetrina?.servizi_consegna && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  Consegna
                </span>
              )}
              {vetrina?.servizi_prenotazione && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  Prenotazione
                </span>
              )}
              {vetrina?.servizi_appuntamento && (
                <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                  Appuntamento
                </span>
              )}
              {vetrina?.servizi_pagamenti_digitali && (
                <span className="px-3 py-1 bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full">
                  Pagamenti Digitali
                </span>
              )}
            </div>
          </div>
        )}

        {vetrina?.cashback && vetrina?.cashback > 0 && (
          <div className="mt-4">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-lg text-center">
              <span className="font-bold text-lg">🎁 Cashback {vetrina?.cashback}%</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VetrinaCard;